import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { TermsheetData } from "../data/termsheetData";
import { loadNotoSans } from "../assets/fonts/loadNotoSans";
import { loadMontserrat } from "../assets/fonts/loadMontserrat";

interface CustomRow {
  label: string;
  value: string;
}

/** Options to align PDF with drawer logic (frequency labels, GST passback, etc.). */
export interface TermsheetPDFOptions {
  /** If false, GST Rebate and Final PTPM/PTPQ rows are omitted. Default true. */
  gstPassbackEnabled?: boolean;
  /** If true, use Quarter labels and PTPQ; else Months and PTPM. Default derived from data.paymentFrequency. */
  isQuarterly?: boolean;
}

const FONT_SIZE = 10;

/** Format date string as DD/MM/YYYY. */
function formatDateDDMMYYYY(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/** Parse numeric value from program limit string (e.g. "INR 50.00 Crores" -> 50). */
function getProgramLimitNumeric(value: string | null | undefined): number {
  const str = value != null ? String(value) : "";
  const cleaned = str.replace(/[^0-9.]/g, "");
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? 0 : num;
}

/** Program limit in rupees for percentage calculations. Handles "INR X Crores", "INR X Lakhs", or raw number. */
function getProgramLimitInRupees(value: string | null | undefined): number {
  const str = (value != null ? String(value) : "").toLowerCase();
  const num = getProgramLimitNumeric(value);
  if (str.includes("crore") || str.includes("cr")) return num * 1_00_00_000;
  if (str.includes("lakh") || str.includes("lac")) return num * 1_00_000;
  return num;
}

/** Parse percentage from string (e.g. "10%" -> 10). */
function getPct(value: string | null | undefined): number {
  const str = value != null ? String(value) : "";
  const cleaned = str.replace(/[^0-9.]/g, "");
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? 0 : num;
}

/** Format number as "INR X.XX lakhs" or "INR X.XX crores". */
function formatInrLakhsCrores(num: number): string {
  if (num >= 1_00_00_000) {
    const cr = num / 1_00_00_000;
    return `INR ${cr % 1 === 0 ? cr.toFixed(0) : cr.toFixed(2)} crores`;
  }
  if (num >= 1_00_000) {
    const l = num / 1_00_000;
    return `INR ${l % 1 === 0 ? l.toFixed(0) : l.toFixed(2)} lakhs`;
  }
  return `INR ${num.toLocaleString("en-IN")}`;
}

/** Format tenure value: "24" -> "24 Month" or "8 Quarter" depending on isQuarterly. */
function formatTenureValue(raw: string, isQuarterly: boolean): string {
  const cleaned = String(raw).replace(/[^0-9.]/g, "");
  const num = parseFloat(cleaned);
  if (Number.isNaN(num)) return raw;
  if (isQuarterly) {
    const quarters = Math.round(num / 3);
    return `${quarters} ${quarters === 1 ? "Quarter" : "Quarters"}`;
  }
  const months = Math.round(num);
  return `${months} ${months === 1 ? "Month" : "Months"}`;
}

/** Format a numeric string or number into Indian lakhs/crores notation (same as drawer). */
function formatIndianCurrency(value: string | number | null | undefined): string {
  const str = value != null ? String(value) : "";
  const cleaned = str.replace(/[^0-9.]/g, "");
  const num = parseFloat(cleaned);
  if (Number.isNaN(num)) return str;
  if (num >= 1_00_00_000) {
    const cr = num / 1_00_00_000;
    return `₹${cr % 1 === 0 ? cr.toFixed(0) : cr.toFixed(2)} Cr`;
  }
  if (num >= 1_00_000) {
    const l = num / 1_00_000;
    return `₹${l % 1 === 0 ? l.toFixed(0) : l.toFixed(2)} L`;
  }
  return `₹${num.toLocaleString("en-IN")}`;
}
const HEADER_HEIGHT = 19;
const FOOTER_HEIGHT = 27;

// Logo ratio tuned to match Astrafin mark and avoid vertical stretch.
const LOGO_HEADER_WIDTH_MM = 40;
const LOGO_HEADER_HEIGHT_MM = 12;

// Footer color (139, 115, 85) – brighter for visibility
const FOOTER_COLOR_RGB: [number, number, number] = [180, 155, 120];

function addHeader(doc: jsPDF, logoDataUrl?: string) {
  const pageWidth = doc.internal.pageSize.getWidth();
  if (logoDataUrl) {
    const x = (pageWidth - LOGO_HEADER_WIDTH_MM) / 2;
    doc.addImage(logoDataUrl, "PNG", x, 3, LOGO_HEADER_WIDTH_MM, LOGO_HEADER_HEIGHT_MM);
  }
  // Divider line under header
  doc.setDrawColor(...FOOTER_COLOR_RGB);
  doc.setLineWidth(0.4);
  doc.line(15, HEADER_HEIGHT, pageWidth - 15, HEADER_HEIGHT);
}

function addFooter(doc: jsPDF, pageNum: number, totalPages: number, fontName: string = "helvetica") {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const footerY = pageHeight - FOOTER_HEIGHT;

  // Divider line above footer
  doc.setDrawColor(...FOOTER_COLOR_RGB);
  doc.setLineWidth(0.4);
  doc.line(15, footerY, pageWidth - 15, footerY);

  const marginX = 15;
  const labelY = footerY + 6;
  const valueY = footerY + 11;
  const footerFontSize = 8.5;

  doc.setFontSize(footerFontSize);
  doc.setTextColor(...FOOTER_COLOR_RGB);

  // Company + CIN (left)
  doc.setFont(fontName, "bold");
  doc.text("Astrafin Neosolutions Pvt. Ltd.", marginX, labelY);
  doc.text("CIN : ", marginX, valueY);
  const cinLabelW = doc.getTextWidth("CIN : ");
  doc.setFont(fontName, "normal");
  doc.text("U66190MH2024PTC432478", marginX + cinLabelW, valueY);

  // Contact Details — centered on page
  doc.setFont(fontName, "bold");
  const contactTitleW = doc.getTextWidth("Contact Details");
  doc.setFont(fontName, "normal");
  const contactMaxW = Math.max(
    contactTitleW,
    doc.getTextWidth("tarun@astrafin.org"),
    doc.getTextWidth("+91 98692 17190")
  );
  const contactX = pageWidth / 2 - contactMaxW / 2;
  doc.setFont(fontName, "bold");
  doc.text("Contact Details", contactX, labelY);
  doc.setFont(fontName, "normal");
  doc.text("tarun@astrafin.org", contactX, valueY);
  doc.text("+91 98692 17190", contactX, valueY + 4.5);

  // Address (right)
  doc.setFont(fontName, "bold");
  const addressTitleW = doc.getTextWidth("Address");
  doc.setFont(fontName, "normal");
  const addressMaxW = Math.max(
    addressTitleW,
    doc.getTextWidth("L-504, Greenwoods CHS, Andheri East,"),
    doc.getTextWidth("Mumbai, Maharashtra, 400093")
  );
  // Right-aligned to page margin (same as DOCX address placement)
  const addressX = pageWidth - marginX - addressMaxW;
  doc.setFont(fontName, "bold");
  doc.text("Address", addressX, labelY);
  doc.setFont(fontName, "normal");
  doc.text("L-504, Greenwoods CHS, Andheri East,", addressX, valueY);
  doc.text("Mumbai, Maharashtra, 400093", addressX, valueY + 4.5);

  // Page number
  doc.setFontSize(9);
  doc.setTextColor(...FOOTER_COLOR_RGB);
  doc.text(`${pageNum}/${totalPages}`, pageWidth - 15, pageHeight - 8, { align: "right" });
}

export async function generateTermsheetPDF(
  data: TermsheetData,
  customRows: CustomRow[] = [],
  title?: string,
  logoDataUrl?: string,
  options?: TermsheetPDFOptions
) {
  const doc = new jsPDF("p", "mm", "a4");
  loadNotoSans(doc);
  const useMontserrat = await loadMontserrat(doc);
  const fontName = useMontserrat ? "Montserrat" : "helvetica";

  const pageWidth = doc.internal.pageSize.getWidth();
  const contentStartY = HEADER_HEIGHT + 8;
  let y = contentStartY;

  const isQuarterly =
    options?.isQuarterly ??
    String(data.paymentFrequency || "").toLowerCase().includes("quarterly");
  const gstPassbackEnabled = options?.gstPassbackEnabled !== false;

  const tenureLabel = isQuarterly ? "Tenure of Lease (In Quarter)" : "Tenure of Lease (In Months)";
  const rentalCostLabel = isQuarterly ? "Rental Cost - PTPQ (Excl GST)" : "Rental Cost - PTPM (Excl GST)";
  const finalLabel = isQuarterly ? "Final - PTPQ (Excl. GST)" : "Final - PTPM (Excl. GST)";

  const programLimitRupees = getProgramLimitInRupees(data.programLimit);
  const securityPct = getPct(data.securityDeposit);
  const processingPct = getPct(data.processingFees);
  const securityAmount =
    securityPct > 0 && programLimitRupees > 0 ? programLimitRupees * (securityPct / 100) : null;
  const processingAmount =
    processingPct > 0 && programLimitRupees > 0 ? programLimitRupees * (processingPct / 100) : null;
  const securityDepositDisplay = `${data.securityDeposit}`
    // securityAmount != null ? `${data.securityDeposit} (${formatInrLakhsCrores(securityAmount)})` : data.securityDeposit;
  const processingFeesDisplay =
    processingAmount != null
      ? `${data.processingFees} (${formatInrLakhsCrores(processingAmount)} + GST)`
      : data.processingFees;

  // First page header
  addHeader(doc, logoDataUrl);

  // To section
  doc.setFontSize(FONT_SIZE);
  doc.setFont(fontName, "normal");
  doc.setTextColor(0, 0, 0);
  doc.text("To,", 15, y);
  y += 5;
  doc.text(data.lesseeName, 15, y);
  y += 5;
  doc.text(data.lesseeAddress, 15, y);
  y += 5;
  doc.text(`Date: ${formatDateDDMMYYYY(data.date)}`, 15, y);
  y += 8;
  doc.text(`Ref. No: ${data.refNo}`, 15, y);
  y += 10;

  // Title
  doc.setFontSize(12);
  doc.setFont(fontName, "bold");
  doc.text(title || "Indicative Termsheet", pageWidth / 2, y, { align: "center" });
  y += 8;

  // Subject (with underline)
  doc.setFontSize(FONT_SIZE);
  doc.setFont(fontName, "bold");
  const subjectText = `Sub: Asset Leasing Facility for ${data.lesseeName}`;
  doc.text(subjectText, 15, y);
  const subjectWidth = doc.getTextWidth(subjectText);
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  doc.line(15, y + 0.6, 15 + subjectWidth, y + 0.6);
  y += 6;
  doc.setFont(fontName, "normal");
  doc.text(
    "Astrafin Neosolutions Private Limited hereby offers an asset leasing facility, subject to the terms and conditions set out below.",
    15, y, { maxWidth: pageWidth - 30 }
  );
  y += 12;

  const tableRows: [string, string][] = [
    ["Lessee Name", data.lesseeName],
    ["Lessor", data.lessor],
    [tenureLabel, formatTenureValue(data.tenureOfLease, isQuarterly)],
    ["Program Limit", formatIndianCurrency(data.programLimit)],
    ["Security Deposit", securityDepositDisplay],
    [rentalCostLabel, `${data.rentalCostPTPM}`],
  ];
  if (gstPassbackEnabled) {
    tableRows.push(["GST Rebate", `${data.gstRebate}`]);
    tableRows.push([finalLabel, `${data.rs_ptpm ?? data.finalPTPM}`]);
  }
  if (processingPct > 0) {
    tableRows.push(["Processing Fees (Excl. GST)", processingFeesDisplay]);
  }
  if (String(data.advanceRental || "").trim() !== "") {
    tableRows.push(["Advance Rental (Excl. GST)", `${data.advanceRental}`]);
  }
  tableRows.push(
    ["Payment Frequency", data.paymentFrequency],
    ["Rental Payments", data.rentalPayments],
    ["Pro Rated Rentals", data.proRatedRentals],
    ["Foreclosure Charges", data.foreclosureCharges],
    ["Documentation Charges", data.documentationCharges],
    ["Insurance", data.insurance],
    ["Clear Title and Encumbrances", data.clearTitle],
    ["Economic Life Certification", data.economicLife],
    ["Penal Charges", data.penalCharges],
    ["Additional Security", data.additionalSecurity],
    ["Minimum Performance Guarantee", data.minimumPerformanceGuarantee],
    ["Validity of Sanction", data.validityOfSanction],
    ["SMA/NPA Reporting", data.smaReporting],
    ["Confidentiality", data.confidentiality]
  );

  // Append custom rows (Added Terms)
  customRows.forEach((cr) => {
    if (cr.label || cr.value) {
      tableRows.push([cr.label || "—", cr.value || "—"]);
    }
  });

  // Row index for highlight: Final PTPM/PTPQ when gstPassbackEnabled, else Rental Cost - PTPM/PTPQ
  const finalPtpRowIndex = gstPassbackEnabled ? tableRows.findIndex((r) => r[0] === finalLabel) : -1;
  const rentalCostRowIndex = tableRows.findIndex((r) => r[0] === rentalCostLabel);
  const highlightRowIndex = gstPassbackEnabled ? finalPtpRowIndex : (rentalCostRowIndex >= 0 ? rentalCostRowIndex : -1);

  autoTable(doc, {
    startY: y,
    head: [],
    body: tableRows,
    theme: "grid",
    styles: { fontSize: FONT_SIZE, cellPadding: 3, lineColor: [200, 200, 200], lineWidth: 0.3, font: fontName },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 55, fillColor: [245, 245, 248], font: fontName },
      1: { cellWidth: "auto", font: fontName },
    },
    margin: { top: HEADER_HEIGHT + 8, bottom: FOOTER_HEIGHT + 5 },
    didParseCell: (hookData) => {
      const isHighlight =
        highlightRowIndex >= 0 && hookData.row.index === highlightRowIndex;
      // Highlight + bold only Final PTPM/PTPQ (or Rental Cost when no GST passback)
      if (isHighlight) {
        hookData.cell.styles.fillColor = [248, 246, 241] as [number, number, number];
        hookData.cell.styles.fontStyle = "bold";
      }
      if (hookData.column.index === 1) {
        const cellValue = String(hookData.cell.raw ?? hookData.cell.text ?? "");
        if (cellValue.includes("₹")) {
          if (isHighlight) {
            // Final row only: custom draw ₹ normal + amount bold
            (hookData.cell as { _finalRupeeText?: string })._finalRupeeText = cellValue;
            hookData.cell.text = [];
            hookData.cell.styles.font = "NotoSans";
            hookData.cell.styles.fontStyle = "normal";
          } else {
            // Other currency rows: whole value NotoSans normal (not bold)
            hookData.cell.styles.font = "NotoSans";
            hookData.cell.styles.fontStyle = "normal";
          }
        } else {
          hookData.cell.styles.font = fontName;
        }
      } else {
        hookData.cell.styles.font = fontName;
      }
    },
    didDrawCell: (hookData) => {
      const rupeeText = (hookData.cell as { _finalRupeeText?: string })._finalRupeeText;
      if (hookData.section !== "body" || hookData.column.index !== 1 || !rupeeText) return;

      const rupeeIdx = rupeeText.indexOf("₹");
      const prefix = rupeeIdx > 0 ? rupeeText.slice(0, rupeeIdx) : "";
      const amount = rupeeText.slice(rupeeIdx + 1);
      const cell = hookData.cell;
      const padL =
        typeof cell.padding === "function" ? (cell.padding("left") as number) : 3;

      // Left-aligned like other rows; vertically middle of the cell
      let x = cell.x + padL;
      const textY = cell.y + cell.height / 2;

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(FONT_SIZE);
      if (prefix) {
        doc.setFont(fontName, "bold");
        doc.text(prefix, x, textY, { baseline: "middle" });
        x += doc.getTextWidth(prefix);
      }
      // ₹ NotoSans normal — same vertical middle as bold amount (top/bottom centered together)
      doc.setFont("NotoSans", "normal");
      const rupeeW = doc.getTextWidth("₹");
      doc.text("₹", x, textY, { baseline: "middle" });
      x += rupeeW;
      doc.setFont(fontName, "bold");
      doc.text(amount, x, textY, { baseline: "middle" });
    },
    didDrawPage: () => {
      addHeader(doc, logoDataUrl);
    },
  });

  // Note
  const finalY = (doc as any).lastAutoTable.finalY + 10;
  doc.setFontSize(FONT_SIZE);
  doc.setFont(fontName, "italic");
  doc.setTextColor(0, 0, 0);
  doc.text(`Note: ${data.note}`, 15, finalY, { maxWidth: pageWidth - 30 });

  // Signature (reduced gap note→label 40%; +20% space label→line for signing)
  const sigY = finalY + 24;
  doc.setFont(fontName, "normal");
  doc.setFontSize(FONT_SIZE);
  doc.text("Accepted and Signed by", 15, sigY);
  doc.text("_______________________________________", 15, sigY + 29);
  doc.text("Name:", 15, sigY + 40);
  doc.text("Designation:", 15, sigY + 49);
  doc.text("Date:", 15, sigY + 58);

  // Add footer with page numbers to all pages
  const totalPages = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(doc, i, totalPages, fontName);
  }

   // Generate filename: companyName_TS_{date}_{tenure}{M|Q}.pdf
   const generateFileName = () => {
    // Format date as ddMMMyy (e.g., 25Dec25)
  
  // Get company name, sanitize for filename (remove special chars, spaces)
  const sanitizedCompanyName = (data.lesseeName || 'Company')
    .replace(/[^a-zA-Z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  
  // Get tenure months
  let tenureMonths = 0;
  let frequencySuffix = '';
  if(data.paymentFrequency?.toLowerCase() === 'quarterly in advance'){
    tenureMonths = parseInt(data.tenureOfLease) / 3;
    frequencySuffix = 'Q';
  }
  else{
    tenureMonths = parseInt(data.tenureOfLease);
    frequencySuffix = 'M';
  }
  return `${sanitizedCompanyName}_TS_${data.date}_${tenureMonths}${frequencySuffix}.pdf`;
};

  doc.save(generateFileName());

  // doc.save(`Termsheet_${data.refNo.replace(/[\s/]/g, "_")}.pdf`);
}
